import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, DeleteModal, Form, AdminFormAction } from '@ui';
import { UPDATE_TX_USER_FORM_DEFAULT_VALUES, updateTxUserSchema } from '@lp/features/admin/forms/TXUserFormBase';
import { TXUsersForm } from '@lp/features/admin/forms/TXUsersForm/TXUsersForm';
import { AuthContext } from '@lp/core/auth';
import { retrieveUnifiedError } from '@/api/utils';
import { UpdateTXUserFormValues } from '@/types/customer';
import { useUpdateUserCustomerMutation } from '@/queries/customer';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { useUploadFile } from '@/hooks/useUploadFile';
import { getChangedValues } from '@/utils/helpers';
import { useWrappedRemoveCustomerMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';
import { CustomerMT, UpdateTxUser } from '@/api/apis/customer';

export const TXUsersEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<CustomerMT>();
  const { mutateAsync } = useUpdateUserCustomerMutation();
  const { uploadFiles } = useUploadFile(FileType.Customer);
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.TxCenterCreate);

  const defaultValues = useMemo<UpdateTXUserFormValues>(() => {
    return {
      ...UPDATE_TX_USER_FORM_DEFAULT_VALUES,
      firstName: data?.firstName || '',
      lastName: data?.lastName || '',
      email: data?.email || '',
      photoPath: data?.photoPath || '',
      primaryPhone: data?.primaryPhone || '',
      secondaryPhone: data?.secondaryPhone || '',
      id: data?.id || '',
      enabled: data?.enabled,
      customerInTxCenters: data?.customerInTxCenters?.map(({ customerId, ...c }) => c) || [],
    };
  }, [data]);

  const handleBackToList = () => {
    navigate('/admin/customers/tx_users');
  };

  const handleSubmit = async (values: UpdateTXUserFormValues) => {
    try {
      let uploadedFile;

      if (values.photo) {
        uploadedFile = await uploadFiles([values.photo]);
      }

      const updateBody = {
        firstName: values?.firstName,
        lastName: values?.lastName,
        email: values?.email,
        photoPath: uploadedFile ? uploadedFile[0].fileName : values.photoPath,
        primaryPhone: values?.primaryPhone,
        secondaryPhone: values?.secondaryPhone,
        enabled: values?.enabled,
        customerInTxCenters: values?.customerInTxCenters?.map(({ txCenter, ...c }) => c) || [],
      };

      const changedValues = getChangedValues<Partial<UpdateTxUser>>(updateBody, defaultValues);

      await mutateAsync({
        id: data.id as string,
        ...changedValues,
      });

      toast.success(`TX User updated successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const {
    isOpen,
    isLoading: isCustomerDeleting,
    onModalOpen,
    onModalClose,
    onEntityDelete,
  } = useEntityDelete({
    query: useWrappedRemoveCustomerMutation,
    successMessage: 'TX User deleted successfully.',
    onDeleteCb: handleBackToList,
  });

  const handleTXUserDelete = () => {
    onModalOpen(data.id as string);
  };

  return (
    <>
      <Form<UpdateTXUserFormValues> defaultValues={defaultValues} schema={updateTxUserSchema} onSubmit={handleSubmit}>
        <div className="flex flex-col gap-9">
          <AdminPageHeader title="Edit TX User" backToListLink="/admin/customers/tx_users">
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handleTXUserDelete}
              hasCreatePermissions={hasCreatePermissions}
              hasEditPermissions={hasCreatePermissions}
              hasDeletePermissions={hasCreatePermissions}
            />
          </AdminPageHeader>
          <TXUsersForm isEditMode isReadOnly={!hasCreatePermissions} />
        </div>
      </Form>
      <DeleteModal
        title="Delete TX User?"
        description="Are you sure you want to delete the TX User?"
        isOpen={isOpen}
        isLoading={isCustomerDeleting}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
